class PdpDrawer extends HTMLElement {
  constructor() {
    super();
    this._onKeydown = this._onKeydown.bind(this);
    this._previouslyFocused = null;
  }

  connectedCallback() {
    this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
  }

  open() {
    this._previouslyFocused = document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });
    document.body.classList.add('pdp-drawer-open');
    window.ScrollLock && ScrollLock.lock('pdp-drawer');
    document.addEventListener('keydown', this._onKeydown);

    requestAnimationFrame(() => {
      const panel = this.querySelector('.pdp-drawer__panel');
      if (panel) panel.focus();
    });
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('pdp-drawer-open');
    window.ScrollLock && ScrollLock.unlock('pdp-drawer');
    document.removeEventListener('keydown', this._onKeydown);

    if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
      this._previouslyFocused.focus();
    }
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = this.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }
}

if (!customElements.get('shipping-info-drawer')) {
  customElements.define('shipping-info-drawer', class extends PdpDrawer {});
}
// Reviews drawer (Bazaarvoice) — only present in the DOM when the theme setting
// "Feature flags → Enable product reviews" is on. Defining the element is
// harmless when it is off (no <reviews-drawer> is rendered).
if (!customElements.get('reviews-drawer')) {
  customElements.define('reviews-drawer', class extends PdpDrawer {});
}
if (!customElements.get('material-info-drawer')) {
  customElements.define('material-info-drawer', class extends PdpDrawer {});
}
if (!customElements.get('find-in-store-drawer')) {
  customElements.define('find-in-store-drawer', class extends PdpDrawer {});
}

class LegalGuaranteeDrawer extends PdpDrawer {
  constructor() {
    super();
    this._zoom = 1;
    this._baseWidth = 0;
    this._baseHeight = 0;
    this._onResize = this._onResize.bind(this);
    this._zoomOut = this._zoomOut.bind(this);
    this._resetZoom = this._resetZoom.bind(this);
    this._zoomIn = this._zoomIn.bind(this);
  }

  connectedCallback() {
    // The snippet is rendered inside the sticky PDP information column. Moving
    // the dialog to <body> keeps that ancestor's stacking context from painting
    // product copy above the fullscreen viewer.
    if (this.parentElement !== document.body) {
      const staleDrawer = [...document.body.children].find((element) => element.id === this.id);
      staleDrawer?.remove();
      document.body.appendChild(this);
      return;
    }

    super.connectedCallback();
    this._viewport = this.querySelector('.pdp-legal-guarantee__notice-scroll');
    this._image = this.querySelector('.pdp-legal-guarantee__notice');
    this._zoomOutButton = this.querySelector('[data-legal-guarantee-zoom-out]');
    this._zoomResetButton = this.querySelector('[data-legal-guarantee-zoom-reset]');
    this._zoomInButton = this.querySelector('[data-legal-guarantee-zoom-in]');

    this._zoomOutButton?.addEventListener('click', this._zoomOut);
    this._zoomResetButton?.addEventListener('click', this._resetZoom);
    this._zoomInButton?.addEventListener('click', this._zoomIn);
    window.addEventListener('resize', this._onResize);
    this._updateZoomControls();
  }

  disconnectedCallback() {
    this._zoomOutButton?.removeEventListener('click', this._zoomOut);
    this._zoomResetButton?.removeEventListener('click', this._resetZoom);
    this._zoomInButton?.removeEventListener('click', this._zoomIn);
    window.removeEventListener('resize', this._onResize);
  }

  open() {
    this._setHeaderOffset();
    super.open();
    requestAnimationFrame(() => this._resetZoom());
  }

  close() {
    super.close();
    this._resetZoom();
  }

  _setHeaderOffset() {
    const header = document.querySelector('.section-header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    this.style.setProperty('--legal-guarantee-header-offset', `${headerBottom}px`);
  }

  _measureBaseSize() {
    if (!this._image) return;

    this._viewport?.removeAttribute('data-zoomed');
    this._image.classList.remove('is-zoomed');
    this._image.style.removeProperty('width');
    this._image.style.removeProperty('height');

    const { width, height } = this._image.getBoundingClientRect();
    const mobileBaseScale = window.matchMedia('(max-width: 989px)').matches ? 0.85 : 1;
    this._baseWidth = width * mobileBaseScale;
    this._baseHeight = height * mobileBaseScale;

    if (mobileBaseScale < 1) {
      this._image.style.width = `${Math.round(this._baseWidth)}px`;
      this._image.style.height = `${Math.round(this._baseHeight)}px`;
    }
  }

  _setZoom(nextZoom) {
    if (!this._image || !this._viewport) return;

    const minZoom = 1;
    const maxZoom = 2;
    const zoom = Math.min(maxZoom, Math.max(minZoom, nextZoom));
    const centerX = (this._viewport.scrollLeft + this._viewport.clientWidth / 2) /
      Math.max(this._viewport.scrollWidth, 1);
    const centerY = (this._viewport.scrollTop + this._viewport.clientHeight / 2) /
      Math.max(this._viewport.scrollHeight, 1);

    if (zoom === 1) {
      this._measureBaseSize();
      this._viewport.scrollTo({ top: 0, left: 0 });
    } else {
      if (!this._baseWidth || !this._baseHeight || this._zoom === 1) {
        this._measureBaseSize();
      }

      this._viewport.setAttribute('data-zoomed', '');
      this._image.classList.add('is-zoomed');
      // Pixel values derive from the rendered official artwork, not a layout constant.
      this._image.style.width = `${Math.round(this._baseWidth * zoom)}px`;
      this._image.style.height = `${Math.round(this._baseHeight * zoom)}px`;

      requestAnimationFrame(() => {
        this._viewport.scrollLeft = centerX * this._viewport.scrollWidth - this._viewport.clientWidth / 2;
        this._viewport.scrollTop = centerY * this._viewport.scrollHeight - this._viewport.clientHeight / 2;
      });
    }

    this._zoom = zoom;
    this._updateZoomControls();
  }

  _zoomOut() {
    this._setZoom(this._zoom - 0.25);
  }

  _resetZoom() {
    this._setZoom(1);
  }

  _zoomIn() {
    this._setZoom(this._zoom + 0.25);
  }

  _updateZoomControls() {
    if (this._zoomResetButton) {
      this._zoomResetButton.textContent = `${Math.round(this._zoom * 100)}%`;
    }
    if (this._zoomOutButton) this._zoomOutButton.disabled = this._zoom <= 1;
    if (this._zoomInButton) this._zoomInButton.disabled = this._zoom >= 2;
  }

  _onResize() {
    this._setHeaderOffset();
    this._resetZoom();
  }
}

if (!customElements.get('legal-guarantee-drawer')) {
  customElements.define('legal-guarantee-drawer', LegalGuaranteeDrawer);
}

function initSidebarCtas() {
  const toast = document.querySelector('[data-pdp-wishlist-toast]');

  const hideWishlistToast = () => {
    if (!toast) return;

    toast.classList.remove('is-visible');
    toast.setAttribute('aria-hidden', 'true');
  };

  const showWishlistToast = () => {
    if (!toast) return;

    const TOAST_MS = 3600;
    clearTimeout(toast.__wishlistToastTimer);
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    toast.setAttribute('aria-hidden', 'false');
    toast.__wishlistToastTimer = setTimeout(hideWishlistToast, TOAST_MS);
    // Auto-hide countdown sweep over the black button pill (AAS-1166).
    window.toastProgress?.(toast, TOAST_MS, '.pdp-wishlist-toast__button');
  };

  toast?.querySelector('[data-drawer-open="WishlistDrawer"]')?.addEventListener('click', hideWishlistToast);

  document.querySelectorAll('[data-open-shipping-modal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const drawer = document.querySelector('.pdp-drawer--shipping');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });
  });

  document.querySelectorAll('[data-open-legal-guarantee]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const drawerId = btn.getAttribute('aria-controls');
      const drawer = drawerId ? document.getElementById(drawerId) : null;
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });
  });

  document.addEventListener(
    'click',
    (e) => {
      const link = e.target.closest('[data-shipping-drawer-link] a');
      if (!link) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const drawer = document.querySelector('.pdp-drawer--shipping');
      if (drawer && typeof drawer.open === 'function') {
        e.preventDefault();
        drawer.open();
      }
    },
    true
  );

  document.querySelectorAll('[data-open-reviews-modal]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const drawer = document.querySelector('.pdp-drawer--reviews');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });
  });

  document.querySelectorAll('[data-open-material-modal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const drawer = document.getElementById(btn.getAttribute('data-open-material-modal'));
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });
  });

  document.querySelectorAll('[data-open-find-in-store]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const drawer = document.querySelector('.pdp-drawer--find-in-store');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    });
  });

  // [data-find-in-store] click handling lives in the inline script inside
  // snippets/modal-find-in-store-drawer.liquid (single owner — was double-bound).

  document.querySelectorAll('[data-purchase-bundle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('pdp:purchase-bundle', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-ask-question]').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('pdp:ask-question', { bubbles: true }));
    });
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const variant = data && data.variant;
      if (!variant) return;
      document
        .querySelectorAll('.pdp-sidebar-ctas__wishlist-btn[data-wishlist-toggle]')
        .forEach((btn) => { btn.dataset.variantId = String(variant.id); });
    });
  }

  document.querySelectorAll('.pdp-sidebar-ctas__wishlist-btn[data-wishlist-toggle]').forEach((btn) => {
    btn.addEventListener(
      'click',
      () => {
        const wasOn = btn.classList.contains('is-wishlisted') || btn.getAttribute('aria-pressed') === 'true';
        if (wasOn) return;

        let attempts = 0;
        const poll = setInterval(() => {
          attempts += 1;
          const isOn = btn.classList.contains('is-wishlisted') || btn.getAttribute('aria-pressed') === 'true';

          if (isOn) {
            clearInterval(poll);
            showWishlistToast();
          } else if (attempts > 20) {
            clearInterval(poll);
          }
        }, 60);
      },
      true
    );
  });

}
class PdpStickyCtas extends HTMLElement {
  constructor() {
    super();
    this._handleScroll = this._handleScroll.bind(this);
    this._handleVariantChange = this._handleVariantChange.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._activePopup = null;
    this._hoverTimeout = null;
    this._closeTimeout = null;
    this._isDesktop = window.matchMedia('(min-width: 1025px)');
  }

  connectedCallback() {
    this.sectionId = this.dataset.section;
    // Scroll trigger = the main buy zone. On dealer/catalog markets
    // (localization.country.market.handle == 'international') buy-buttons.liquid
    // renders the "Buy from dealer" CTA instead of the ATC submit button, so
    // [id^="ProductSubmitButton-"] does not exist. Fall back to the buy-zone
    // container (.product-form__buttons), which is present in both cases, so the
    // bar still reveals on scroll for size/colour browsing.
    this.targetElement =
      document.querySelector('[id^="ProductSubmitButton-"]') ||
      document.querySelector(`#MainProduct-${this.sectionId} .product-form__buttons`) ||
      document.querySelector('.product-form__buttons');
    this.footerElement = document.querySelector('footer');

    try {
      this._variants = JSON.parse(this.dataset.variants || '[]');
    } catch (e) {
      this._variants = [];
    }

    window.addEventListener('scroll', this._handleScroll, { passive: true });
    document.body.addEventListener('change', this._handleVariantChange);
    document.addEventListener('click', this._onDocumentClick);

    requestAnimationFrame(() => this._handleScroll());

    // The bar never adds to cart itself — it clicks the ONE real submit button so
    // there is a single <product-form>, a single hidden variant id and a single
    // code path into /cart/add. Scoped to this section: a quick view or a compare
    // form elsewhere on the page also renders a ProductSubmitButton-*, and an
    // unscoped lookup could hand the click to that product instead.
    const stickyBtn = this.querySelector('[data-sticky-atc]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const mainBtn =
          document.querySelector(`#MainProduct-${this.sectionId} [id^="ProductSubmitButton-"]`) ||
          document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (mainBtn && !mainBtn.disabled) {
          mainBtn.click();
        }
      });

      this._initLoadingMirror(stickyBtn);
    }

    this._initHoverTriggers();
    this._initSizeButtons();
    this._initColorButtons();
    this._initPopupNavigation();
    this._initCloseButtons();
    this._initBackdrop();
    this._initSizeguideHeightTween();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._handleScroll);
    document.body.removeEventListener('change', this._handleVariantChange);
    document.removeEventListener('click', this._onDocumentClick);
    this._loadingObserver?.disconnect();
  }

  /**
   * AAS-5285. The bar delegates the add to the main submit button, which owns
   * the only loading state — and which is, by definition, scrolled out of view
   * whenever the bar is showing. The click therefore looked like it did
   * nothing until the cart drawer opened.
   *
   * The class is mirrored rather than set on click, so the spinner follows the
   * real request: it clears when product-form.js clears it, including on the
   * error path, and it never appears when the proxied click is a no-op.
   *
   * Observed on the section subtree, not on the button node — the section
   * re-renders its submit button on variant change (see _syncBuyState), which
   * would leave an observer bound to the old node watching a detached element.
   *
   * The mirrored class is the theme's own `loading`, not an `is-` variant: the
   * spinner is hidden by `.loading__spinner { opacity: 0 }` and revealed only
   * by `.loading .loading__spinner` (theme.css). Nothing else keys off a bare
   * `.loading`, and this button carries no `.btn`/`.button`, so none of the
   * theme's other loading treatments reach it.
   */
  _initLoadingMirror(stickyBtn) {
    const mainSection = document.getElementById(`MainProduct-${this.sectionId}`);
    if (!mainSection) return;

    const spinner = stickyBtn.querySelector('.loading__spinner');

    this._loadingObserver = new MutationObserver((records) => {
      for (const record of records) {
        const target = record.target;
        if (!target.matches?.('[id^="ProductSubmitButton-"]')) continue;
        const isLoading = target.classList.contains('loading');
        stickyBtn.classList.toggle('loading', isLoading);
        spinner?.classList.toggle('hidden', !isLoading);
      }
    });

    this._loadingObserver.observe(mainSection, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  _initHoverTriggers() {
    this.querySelectorAll('[data-sticky-hover-trigger]').forEach((trigger) => {
      const popupName = trigger.dataset.stickyHoverTrigger;
      const popup = this.querySelector(`[data-sticky-popup="${popupName}"]`);
      if (!popup) return;

      trigger.addEventListener('mouseenter', () => {
        if (!this._isDesktop.matches) return;
        clearTimeout(this._closeTimeout);
        this._hoverTimeout = setTimeout(() => this._openPopup(popupName), 80);
      });

      trigger.addEventListener('mouseleave', (e) => {
        if (!this._isDesktop.matches) return;
        clearTimeout(this._hoverTimeout);
        const related = e.relatedTarget;
        if (related && (popup.contains(related) || popup === related)) return;
        this._closeTimeout = setTimeout(() => this._closePopup(popupName), 200);
      });

      popup.addEventListener('mouseenter', () => {
        clearTimeout(this._closeTimeout);
      });

      popup.addEventListener('mouseleave', (e) => {
        if (!this._isDesktop.matches) return;
        const related = e.relatedTarget;
        if (related && (trigger.contains(related) || trigger === related)) return;
        this._closeTimeout = setTimeout(() => this._closePopup(popupName), 200);
      });

      trigger.addEventListener('click', (e) => {
        if (this._isDesktop.matches) {
          e.preventDefault();
          return;
        }
        const target = document.querySelector(`#MainProduct-${this.sectionId}`);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // The real Shopify option name, rendered onto the popup by
  // snippets/pdp-sticky-ctas-bar.liquid. Guessing 'Size'/'Color'/'Colour' here
  // matched nothing once the option names were translated (Größe, Farbe, …), so
  // the popups silently stopped driving the picker on non-EN storefronts.
  _optionNameFor(popupName) {
    const popup = this.querySelector(`[data-sticky-popup="${popupName}"]`);
    return (popup && popup.dataset.stickyOptionName) || '';
  }

  _initSizeButtons() {
    this.querySelectorAll('[data-sticky-size-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // AAS-5053 follow-up: unavailable ("is-unavailable") is a visual,
        // struck-through state only — same as the desktop swatch picker
        // (product-variant-options.liquid), which never blocks the click.
        // Picking a sold-out size switches to that variant and the buy zone
        // correctly reports "Sold out" instead of doing nothing.
        const value = btn.dataset.stickySizeValue;
        this._selectMainOption(this._optionNameFor('sizes'), value);
        this.querySelectorAll('[data-sticky-size-value]').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
      });
    });
  }

  _initColorButtons() {
    this.querySelectorAll('[data-sticky-color-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        // AAS-5053 follow-up: same non-blocking "is-unavailable" treatment
        // as the size buttons above.
        const value = btn.dataset.stickyColorValue;
        this._selectMainOption(this._optionNameFor('colors'), value);
        this.querySelectorAll('[data-sticky-color-value]').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this._paintColorDot(btn);
      });
    });
  }

  // Paint the collapsed bar's colour dot from the picked swatch button. The server
  // resolves the swatch to a full CSS declaration (data-sticky-color-css) — hex OR
  // background-image for image swatches — so this writes the whole style attribute
  // rather than a single backgroundColor, mirroring updateTriggerLabel() in
  // assets/mobile-option-sheets.js.
  _paintColorDot(btn) {
    const dot = this.querySelector('.pdp-sticky-ctas__section--colors .pdp-sticky-ctas__color-dot');
    if (!dot) return;
    dot.setAttribute('style', btn.dataset.stickyColorCss || '');
  }

  _initPopupNavigation() {
    this.querySelectorAll('[data-sticky-open-sizeguide]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._openPopup('sizeguide');
      });
    });
  }

  _initCloseButtons() {
    this.querySelectorAll('[data-sticky-close-popup]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._closeAllPopups();
      });
    });
  }

  _initBackdrop() {
    const backdrop = this.querySelector('[data-sticky-popup-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => this._closeAllPopups());
    }
  }

  // Smoothly tween the sizeguide popup's height when its content changes size
  // (CM ⇄ INCH swaps a different-height thead/tbody pair, tab switches swap
  // whole panes) instead of snapping. Same pin-and-tween model as
  // assets/info-drawer.js: while open the popup is pinned to an explicit pixel
  // height, the observer waits 100ms for the content to settle, then the CSS
  // height transition runs to the new natural height. The 80vh max-height
  // keeps clamping the pin, so the cap always wins.
  _initSizeguideHeightTween() {
    const popup = this.querySelector('[data-sticky-popup="sizeguide"]');
    if (!popup || typeof ResizeObserver === 'undefined') return;
    this._sizeguidePopup = popup;
    this._sgSettleTimer = 0;

    const observer = new ResizeObserver(() => {
      if (!popup.classList.contains('is-open')) return;
      clearTimeout(this._sgSettleTimer);
      this._sgSettleTimer = setTimeout(() => {
        requestAnimationFrame(() => this._tweenSizeguideHeight());
      }, 100);
    });

    // The panes scroll (overflow-y: auto), so their own boxes track the popup
    // and would loop the observer — watch their children instead, plus the
    // tab bar (same reasoning as info-drawer.js _heightTargets).
    popup.querySelectorAll('.size-guide-content__pane').forEach((pane) => {
      Array.from(pane.children).forEach((child) => observer.observe(child));
    });
    const tabs = popup.querySelector('.size-guide-content__tabs');
    if (tabs) observer.observe(tabs);
  }

  // Pin to the current rendered height (max-height already applied) so the
  // next content change animates from a fixed value instead of auto.
  _pinSizeguideHeight() {
    const popup = this._sizeguidePopup;
    if (!popup || !popup.classList.contains('is-open')) return;
    popup.style.height = '';
    popup.style.height = `${popup.getBoundingClientRect().height}px`;
  }

  _tweenSizeguideHeight() {
    const popup = this._sizeguidePopup;
    if (!popup) return;
    const from = popup.getBoundingClientRect().height;

    // Natural target height with the inline pin momentarily lifted — the
    // stylesheet max-height still clamps it.
    popup.style.height = '';
    const to = popup.getBoundingClientRect().height;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || Math.abs(to - from) < 1) {
      popup.style.height = `${to}px`;
      return;
    }

    popup.style.height = `${from}px`;
    void popup.offsetHeight;
    popup.style.height = `${to}px`;
  }

  // Drop the pin and any pending tween so the next open starts from the new
  // content's natural height.
  _releaseSizeguideHeight() {
    if (!this._sizeguidePopup) return;
    clearTimeout(this._sgSettleTimer);
    this._sizeguidePopup.style.height = '';
  }

  _openPopup(name) {
    this._closeAllPopups();

    const popup = this.querySelector(`[data-sticky-popup="${name}"]`);
    if (!popup) return;

    popup.classList.add('is-open');
    this._activePopup = name;

    if (name === 'sizeguide') {
      requestAnimationFrame(() => this._pinSizeguideHeight());
    }

    const backdrop = this.querySelector('[data-sticky-popup-backdrop]');
    if (backdrop) backdrop.classList.add('is-active');

    const trigger = this.querySelector(`[data-sticky-hover-trigger="${name}"]`);
    if (trigger) trigger.classList.add('is-hover-active');
  }

  _closePopup(name) {
    const popup = this.querySelector(`[data-sticky-popup="${name}"]`);
    if (popup) popup.classList.remove('is-open');
    if (name === 'sizeguide') this._releaseSizeguideHeight();

    const trigger = this.querySelector(`[data-sticky-hover-trigger="${name}"]`);
    if (trigger) trigger.classList.remove('is-hover-active');

    if (this._activePopup === name) {
      this._activePopup = null;
      const backdrop = this.querySelector('[data-sticky-popup-backdrop]');
      if (backdrop) backdrop.classList.remove('is-active');
    }
  }

  _closeAllPopups() {
    this.querySelectorAll('[data-sticky-popup]').forEach((p) => p.classList.remove('is-open'));
    this._releaseSizeguideHeight();
    this.querySelectorAll('[data-sticky-hover-trigger]').forEach((t) => t.classList.remove('is-hover-active'));
    this._activePopup = null;
    const backdrop = this.querySelector('[data-sticky-popup-backdrop]');
    if (backdrop) backdrop.classList.remove('is-active');
  }

  _selectMainOption(optionName, value) {
    if (!optionName) return;
    const section = document.querySelector(`#MainProduct-${this.sectionId}`);
    if (!section) return;

    const radios = section.querySelectorAll(`input[name="${optionName}"], input[name="${optionName.toLowerCase()}"]`);
    for (const radio of radios) {
      if (radio.value === value) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    const selects = section.querySelectorAll(`select[name="options[${optionName}]"], select[name="options[${optionName.toLowerCase()}]"]`);
    for (const sel of selects) {
      for (const opt of sel.options) {
        if (opt.value === value || opt.textContent.trim() === value) {
          sel.value = opt.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
  }

  _onDocumentClick(e) {
    if (this._activePopup && !this.contains(e.target)) {
      this._closeAllPopups();
    }
  }

  _getAbsoluteTop(element) {
    if (!element) return 0;
    let top = 0;
    while (element) {
      top += element.offsetTop || 0;
      element = element.offsetParent;
    }
    return top;
  }

  _handleScroll() {
    if (!this.targetElement) return;

    const targetTop = this._getAbsoluteTop(this.targetElement);
    const footerTop = this.footerElement ? this._getAbsoluteTop(this.footerElement) : Infinity;
    const scrollY = window.scrollY || window.pageYOffset;
    const isTargetOut = targetTop < scrollY;
    const isFooterIn = scrollY + window.innerHeight > footerTop;

    if (isTargetOut && !isFooterIn) {
      this.classList.add('is-visible');
    } else {
      this.classList.remove('is-visible');
      if (this._activePopup) this._closeAllPopups();
    }
  }

  _handleVariantChange(event) {
    const target = event.target;
    if (!this.sectionId) return;

    const productSection = target.closest(`#MainProduct-${this.sectionId}`);
    if (!productSection) return;

    if (target.tagName === 'INPUT' || target.tagName === 'SELECT') {
      const optionName = target.name;
      // .value, never .textContent — VariantSelects.setInputAvailability() rewrites
      // an <option>'s text to "Unavailable - M" for out-of-stock combinations, and
      // that string would end up echoed as the bar's selected size.
      const optionValue =
        target.tagName === 'INPUT' ? target.value : target.options[target.selectedIndex]?.value;
      if (!optionValue) return;

      let displayName = optionName;
      const matches = optionName.match(/options\[(.+)\]/);
      if (matches && matches[1]) {
        displayName = matches[1];
      }

      const display = this.querySelector(`[data-sticky-option="${displayName}"]`);
      if (display) {
        display.textContent = optionValue;
      }

      const sizeBtn = this.querySelector(`[data-sticky-size-value="${optionValue}"]`);
      if (sizeBtn) {
        this.querySelectorAll('[data-sticky-size-value]').forEach((b) => b.classList.remove('is-selected'));
        sizeBtn.classList.add('is-selected');
      }

      const colorBtn = this.querySelector(`[data-sticky-color-value="${optionValue}"]`);
      if (colorBtn) {
        this.querySelectorAll('[data-sticky-color-value]').forEach((b) => b.classList.remove('is-selected'));
        colorBtn.classList.add('is-selected');
        this._paintColorDot(colorBtn);
      }

      // Sync the price + ATC button to the newly-selected variant. theme.js
      // re-renders the main submit button on variant change but never the sticky
      // bar, so without this the sticky ATC stays frozen at page-render state
      // (e.g. shows "sold out"/disabled after switching to an in-stock size).
      // The main form's hidden id input is updated in place by theme.js
      // (updateVariantInput) before its bubbling `change` reaches this handler,
      // so it is the source of truth for the current variant.
      this._syncBuyState();
      this._updateOptionAvailability();
    }
  }

  // AAS-5053: the size/colour popups were rendered once, server-side, and never
  // touched again — a size sold out in the just-picked colour kept showing as
  // tappable here even though the desktop picker (theme.js) struck it out.
  // Recomputes availability from the same variant JSON emitted into
  // data-variants, combination-aware exactly like product-variant-options.liquid
  // (an option value at index N is available only if a variant carries it AND
  // matches the CURRENTLY selected values of every option before index N).
  _isOptionValueAvailable(optionIndex, value, currentVariant) {
    return this._variants.some((variant) => {
      if (!variant.options || variant.options[optionIndex] !== value || !variant.available) return false;
      for (let i = 0; i < optionIndex; i += 1) {
        if (variant.options[i] !== currentVariant.options[i]) return false;
      }
      return true;
    });
  }

  _updateOptionAvailability() {
    // AAS-4164 catalog-only market — every option reads as available there
    // (no disabled styling), same carve-out as the SSR render and theme.js's
    // updateMobileSheetAvailability().
    if (document.documentElement.classList.contains('market--catalog')) return;

    const idInput = document.querySelector(`#MainProduct-${this.sectionId} .product-variant-id`);
    if (!idInput) return;
    const variantId = Number(idInput.value);
    const currentVariant = this._variants.find((v) => v.id === variantId);
    if (!currentVariant || !currentVariant.options) return;

    const sizeGrid = this.querySelector('[data-sticky-size-grid]');
    if (sizeGrid) {
      const sizeIndex = Number(sizeGrid.dataset.stickyOptionIndex);
      if (!Number.isNaN(sizeIndex)) {
        sizeGrid.querySelectorAll('[data-sticky-size-value]').forEach((btn) => {
          const available = this._isOptionValueAvailable(sizeIndex, btn.dataset.stickySizeValue, currentVariant);
          // AAS-5053 follow-up: visual-only, like the desktop swatch picker —
          // picking a sold-out size must stay possible so the buy zone can
          // report "Sold out" instead of the click being silently dropped.
          btn.classList.toggle('is-unavailable', !available);
        });
      }
    }

    const colorPopup = this.querySelector('[data-sticky-popup="colors"]');
    if (colorPopup) {
      const colorIndex = Number(colorPopup.dataset.stickyOptionIndex);
      if (!Number.isNaN(colorIndex)) {
        colorPopup.querySelectorAll('[data-sticky-color-value]').forEach((btn) => {
          const available = this._isOptionValueAvailable(colorIndex, btn.dataset.stickyColorValue, currentVariant);
          // AAS-5053 follow-up: same non-blocking treatment as the size grid.
          btn.classList.toggle('is-unavailable', !available);
        });
      }
    }
  }

  _syncBuyState() {
    const atc = this.querySelector('[data-sticky-atc]');
    // Catalog/dealer markets render neither the price nor the ATC button.
    if (!atc) return;

    const idInput = document.querySelector(`#MainProduct-${this.sectionId} .product-variant-id`);
    if (!idInput) return;

    const variantId = Number(idInput.value);
    const variant = this._variants.find((v) => v.id === variantId);
    if (!variant) return;

    atc.disabled = !variant.available;
    const label = atc.querySelector('[data-sticky-atc-label]');
    if (label) {
      label.textContent = variant.available
        ? atc.dataset.labelAvailable
        : atc.dataset.labelSoldout;
    }

    // variant.price is the pre-formatted money string from data-variants; it is
    // omitted on catalog markets, but those have no ATC button so we never reach
    // here. Update only the amount node, leaving the inline VAT label intact.
    const amount = this.querySelector('[data-sticky-price-amount]');
    if (amount && variant.price != null) {
      amount.textContent = variant.price;
    }
  }
}

if (!customElements.get('pdp-sticky-ctas')) {
  customElements.define('pdp-sticky-ctas', PdpStickyCtas);
}
// Tab switch slides the content (AAS-5265): the open pane rides out toward
// the side you are leaving, then the target pane rides in from the other side
// — direction follows the tab-bar order, like paging a carousel. Sequential
// legs, not an overlap: the panes are normal-flow siblings (overlapping would
// need absolute-positioning games), and two quick legs read cleaner against
// the panel height tween that runs on the same swap (island popup and the
// info-drawer shell both animate height). One implementation serves BOTH
// size-guide views — the island flyout and the full modal.
function initSizeGuideTabs() {
  const LEG_MS = 150;

  // Both size-guide views stack their panes in one grid cell so the window is
  // always as tall as the tallest tab — driven by .is-active + visibility
  // alone. The SSR hidden attributes must go, or the Tailwind preflight's
  // layered `[hidden] {display:none !important}` collapses the stacking.
  document
    .querySelectorAll('.size-guide-content__pane[hidden]')
    .forEach((pane) => pane.removeAttribute('hidden'));

  document.addEventListener('click', (e) => {
    const tabBtn = e.target.closest('[data-size-guide-tab]');
    if (!tabBtn) return;

    const container = tabBtn.closest('[data-size-guide-tabs]');
    if (!container) return;
    if (container.dataset.tabAnimating) return;

    const targetPane = tabBtn.dataset.sizeGuideTab;
    const current = container.querySelector('[data-size-guide-pane].is-active');
    if (current && current.dataset.sizeGuidePane === targetPane) return;

    const tabs = Array.from(container.querySelectorAll('[data-size-guide-tab]'));
    const forward = tabs.indexOf(tabBtn) > tabs.findIndex((b) => b.classList.contains('is-active'));

    tabs.forEach((btn) => {
      const isActive = btn === tabBtn;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });

    // Panes never carry [hidden] (see above) — .is-active plus the
    // :not(.is-active) visibility rule handle them in both views.
    const swap = () => {
      container.querySelectorAll('[data-size-guide-pane]').forEach((pane) => {
        pane.classList.toggle('is-active', pane.dataset.sizeGuidePane === targetPane);
      });
    };

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !current) {
      swap();
      return;
    }

    container.dataset.tabAnimating = '1';
    const outClass = forward ? 'slide-out-left' : 'slide-out-right';
    const inClass = forward ? 'slide-in-right' : 'slide-in-left';

    // Leg 1: transition the open pane from identity to the offset+fade state.
    current.classList.add('is-sliding', outClass);
    setTimeout(() => {
      current.classList.remove('is-sliding', outClass);
      swap();

      // Leg 2: park the target pane at the opposite offset WITHOUT transition
      // (no is-sliding yet), commit that position, then let it ride to
      // identity with the transition on.
      const next = container.querySelector('[data-size-guide-pane].is-active');
      if (!next) {
        delete container.dataset.tabAnimating;
        return;
      }
      next.classList.add(inClass);
      void next.offsetWidth;
      next.classList.add('is-sliding');
      next.classList.remove(inClass);
      setTimeout(() => {
        next.classList.remove('is-sliding');
        delete container.dataset.tabAnimating;
      }, LEG_MS);
    }, LEG_MS);
  });
}
// CM ⇄ INCH toggle — swaps the metric/inch header row AND body together. Scoped to
// the individual table block (.size-guide-block) so multiple tables in one tab don't
// cross-toggle. Every unit-swappable element carries data-unit="metric|inch".
function initSizeGuideUnitToggle() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-size-guide-unit]');
    if (!btn) return;

    const group = btn.closest('[data-size-guide-units]');
    if (!group) return;

    const unit = btn.dataset.sizeGuideUnit;
    const scope = group.closest('.size-guide-block') || group.closest('[data-size-guide-pane]') || document;

    group.querySelectorAll('[data-size-guide-unit]').forEach((b) => {
      const isActive = b.dataset.sizeGuideUnit === unit;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-pressed', String(isActive));
    });

    scope.querySelectorAll('[data-unit]').forEach((el) => {
      el.hidden = el.dataset.unit !== unit;
    });
  });
}
function initSizeGuideScrollIndicator() {
  document.querySelectorAll('[data-size-guide-scroll]').forEach((wrap) => {
    const indicator = wrap.parentElement?.querySelector('.size-guide-content__scroll-indicator');
    if (!indicator) return;

    const thumb = indicator.querySelector('.size-guide-content__scroll-thumb');
    if (!thumb) return;

    function update() {
      const scrollWidth = wrap.scrollWidth;
      const clientWidth = wrap.clientWidth;
      if (scrollWidth <= clientWidth) {
        indicator.style.display = 'none';
        return;
      }
      indicator.style.display = '';
      const ratio = clientWidth / scrollWidth;
      const thumbWidth = Math.max(ratio * 100, 15);
      const scrollLeft = wrap.scrollLeft;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      const maxTranslate = 100 / ratio - 100;

      thumb.style.width = thumbWidth + '%';
      thumb.style.transform = 'translateX(' + (progress * maxTranslate) + '%)';
    }

    wrap.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(wrap);
    requestAnimationFrame(update);

    // Drag-to-scroll (AAS-5265): the thumb is grabbable and a press on the
    // bare track jumps there first — either way the pointer keeps dragging
    // from that position. Bound on the whole indicator so its padding acts
    // as the hit area (the 3px track alone is untargetable). Writing
    // wrap.scrollLeft feeds the existing scroll listener, which repositions
    // the thumb and updates the edge fades.
    const track = indicator.querySelector('.size-guide-content__scroll-track');
    let dragStartX = null;
    let dragStartScroll = 0;

    indicator.addEventListener('pointerdown', (e) => {
      const maxScroll = wrap.scrollWidth - wrap.clientWidth;
      const trackRect = track.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const usable = trackRect.width - thumbRect.width;
      if (maxScroll <= 0 || usable <= 0) return;
      e.preventDefault();

      if (e.clientX < thumbRect.left || e.clientX > thumbRect.right) {
        // Track press: center the thumb on the pointer.
        const target = ((e.clientX - trackRect.left - thumbRect.width / 2) / usable) * maxScroll;
        wrap.scrollLeft = Math.max(0, Math.min(maxScroll, target));
      }
      dragStartX = e.clientX;
      dragStartScroll = wrap.scrollLeft;
      indicator.setPointerCapture(e.pointerId);
      indicator.classList.add('is-dragging');
    });

    indicator.addEventListener('pointermove', (e) => {
      if (dragStartX === null) return;
      const maxScroll = wrap.scrollWidth - wrap.clientWidth;
      const usable = track.getBoundingClientRect().width - thumb.getBoundingClientRect().width;
      if (usable <= 0) return;
      const target = dragStartScroll + ((e.clientX - dragStartX) / usable) * maxScroll;
      wrap.scrollLeft = Math.max(0, Math.min(maxScroll, target));
    });

    const endDrag = () => {
      if (dragStartX === null) return;
      dragStartX = null;
      indicator.classList.remove('is-dragging');
    };
    indicator.addEventListener('pointerup', endDrag);
    indicator.addEventListener('pointercancel', endDrag);
  });
}

// Island size-guide table edge fades (AAS-5265). The island flyout renders the
// SAME table as the full modal; when the narrower popup can't hold its natural
// column widths, the wrap scrolls sideways and this writes data-scroll on it
// (`end` = more to the right, `start` = more to the left, `both`, absent =
// everything fits) so the CSS can fade whichever edge still has content behind
// it. Contract, 2px tolerance and the RTL branch mirror
// assets/breadcrumb-scroll-fade.js; only the island's CSS consumes the
// attribute, so writing it on the modal's wraps too is harmless.
function initSizeGuideScrollFades() {
  const TOLERANCE = 2;

  const update = (wrap) => {
    const { scrollLeft, scrollWidth, clientWidth } = wrap;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= TOLERANCE) {
      wrap.removeAttribute('data-scroll');
      return;
    }

    const isRtl = getComputedStyle(wrap).direction === 'rtl';
    const atStart = isRtl ? Math.abs(scrollLeft) >= maxScroll - TOLERANCE : scrollLeft <= TOLERANCE;
    const atEnd = isRtl ? scrollLeft >= -TOLERANCE : scrollLeft >= maxScroll - TOLERANCE;

    // Name the edge that still has content behind it, not the edge we sit at.
    let state = 'both';
    if (atStart) state = 'end';
    else if (atEnd) state = 'start';
    wrap.setAttribute('data-scroll', state);
  };

  document.querySelectorAll('[data-size-guide-scroll]').forEach((wrap) => {
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update(wrap);
      });
    };

    wrap.addEventListener('scroll', schedule, { passive: true });

    // The CM/INCH toggle swaps thead/tbody pairs and resizes the table with
    // no scroll event; a resize of the popup itself changes the fit too.
    const observer = new ResizeObserver(schedule);
    observer.observe(wrap);
    Array.from(wrap.children).forEach((child) => observer.observe(child));

    schedule();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSidebarCtas();
    initSizeGuideTabs();
    initSizeGuideUnitToggle();
    initSizeGuideScrollIndicator();
    initSizeGuideScrollFades();
  });
} else {
  initSidebarCtas();
  initSizeGuideTabs();
  initSizeGuideUnitToggle();
  initSizeGuideScrollIndicator();
  initSizeGuideScrollFades();
}
